import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import controller from './project.controller'
import i from '../../../translations/i'
import { Titles } from '../../../layouts/Titles'
import { Row, Col, Select, Input, Tooltip, Button, Popconfirm, InputNumber, Switch, notification } from "antd";
import { storeLocal } from '../../../helpers/stores'
import { ErrorByPricingPlan } from '../../../components/ErrorByPricingPlan'
import {
    InfoCircleOutlined, SettingOutlined, DollarOutlined, EnvironmentOutlined, AppstoreOutlined, LayoutOutlined,
    ScanOutlined, BellOutlined, MessageOutlined, UserOutlined, ToolOutlined, LockOutlined
} from '@ant-design/icons'
import priceTypesController from "../price_types/controller"
import userController from "../../user/controllers/user.controller";
import settingsController from "../orders/settings.controller";
import OptionTypesController from "../../../components/options/optionTypes.controller";
import OptionValuesController from "../../../components/options/optionValues.controller";
import { SectionBadge, MasterToggle, ToggleField, FieldBlock, DimGroup } from "../../../components/SectionCard";

const OPTION_KEYS = ['allowTransitionToProductCard', 'hideDeliveryInfo']

const cardStyle: React.CSSProperties = { width: '100%', display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start' }

const PRIMARY_BADGE = 'linear-gradient(135deg, var(--primary-color), #9186e0)'
const DARK_BADGE = 'linear-gradient(135deg, #3f3d56, #63607f)'
const WARNING_BADGE = 'linear-gradient(135deg, #8a5310, #c08234)'

const viewOptions = () => [
    {value: 1, label: i.t('settings:project.selectOptions.list')},
    {value: 2, label: i.t('settings:project.selectOptions.tile')},
    {value: 5, label: i.t('settings:project.selectOptions.horizontal')},
    {value: 6, label: i.t('settings:project.selectOptions.horizontalTight')},
    {value: 9, label: i.t('settings:project.selectOptions.tile2')},
]

const categoryDisplays = () => Array.from({ length: 12 }, (_, index) => ({ label: i.t(`def:displays.e_${index + 1}`), value: index + 1 }))

const numberOptions = (count: number) => Array.from({ length: count }, (_, index) => ({ label: `${index + 1}`, value: index + 1 }))

// Строка-действие в стиле ToggleField: подпись слева, кнопка справа.
const ActionField = ({ label, children }: { label: React.ReactNode, children: React.ReactNode }) => (
    <div style={{
        background: 'var(--surface-field-color)', borderRadius: 12, padding: '10px 16px', width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12
    }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-color)' }}>{label}</span>
        {children}
    </div>
)

const SelectField = observer(({ label, name, options }: { label: React.ReactNode, name: string, options: { label: string, value: any }[] }) => (
    <FieldBlock label={label}>
        <Select
            // @ts-ignore
            value={controller.project[name]}
            style={{ width: '100%' }}
            onChange={e => controller.set(name, e)}
            options={options}
        />
    </FieldBlock>
))

const ProjectToggle = observer(({ label, name, plan }: { label: React.ReactNode, name: string, plan?: number }) => (
    <ToggleField
        label={label}
        // @ts-ignore
        checked={!!controller.project[name]}
        onChange={e => controller.set(name, e)}
        plan={plan}
    />
))

// Опции из OptionValuesController: до первого изменения там лежит DTO с бэка
// ({ value_int, ... }), после — уже само значение. Если опция ещё не сохранялась —
// показываем type.default.
const OptionToggle = observer(({ optionKey }: { optionKey: string }) => {
    const type = OptionTypesController.items.find((t: any) => t.key === optionKey)
    const value = OptionValuesController.items[optionKey]

    if (!type) return null

    const checked = value && typeof value === 'object'
        ? !!value.value_int
        : (value === undefined || value === null ? !!type.default : !!value)

    return (
        <ToggleField
            label={type.label}
            checked={checked}
            onChange={e => OptionValuesController.onChangeValue(optionKey, e)}
        />
    )
})

const GeneralSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<SettingOutlined />} text={i.t('settings:project.sections.general')} background={PRIMARY_BADGE} />
        <FieldBlock label={i.t('settings:project.timezone')}>
            <p style={{ marginBottom: 8 }}>{i.t('settings:project.timezoneHint')}</p>
            <Select
                showSearch
                value={controller.timezone || undefined}
                style={{ width: '100%' }}
                placeholder={i.t('settings:project.timezonePlaceholder')}
                onChange={e => controller.setTimezone(e)}
                options={controller.timezones}
                filterOption={(input, option) => `${option?.label ?? ''}`.toLowerCase().includes(input.toLowerCase())}
            />
        </FieldBlock>
        <FieldBlock label={i.t('def:phoneCode')}>
            <Input
                value={controller.project.default_dialing_code}
                onChange={e => controller.set('default_dialing_code', e.target.value)}
            />
        </FieldBlock>
    </div>
))

const PricesSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<DollarOutlined />} text={i.t('settings:project.sections.prices')} background={DARK_BADGE} />
        <MasterToggle>
            <ToggleField
                label={i.t('def:Product prices')}
                checked={!!controller.project.price_types_enabled}
                onChange={e => {
                    if(!priceTypesController.items || priceTypesController.items.length === 0) {
                        notification.error({
                            message: i.t('def:errors.error'),
                            description: i.t('settings:project.createPriceTypesFirst')
                        });
                        return
                    }
                    controller.set('price_types_enabled', e)
                }}
            />
        </MasterToggle>
        {!!controller.project.price_types_enabled && priceTypesController.items.length > 0 &&
            <FieldBlock label={i.t('settings:project.defaultPriceType')}>
                <Select
                    value={controller.project.default_type_price_id ?? priceTypesController.items[0].id ?? null}
                    style={{ width: '100%' }}
                    onChange={e => controller.set('default_type_price_id', e)}
                    options={priceTypesController.items.map(el => ({
                        label: `${el.name} (${el.private_name})`,
                        value: el.id
                    }))}
                />
            </FieldBlock>
        }
        <ProjectToggle label={i.t('settings:project.hide_prices_un_users')} name="hide_prices_un_users" />
        {controller.project.price_rounding_step !== undefined && <PriceRoundingFields />}
    </div>
))

const PRICE_ROUNDING_STEPS = [0, 1, 5, 10, 50, 100]

// Округление цен для покупателя: каталог и позиции корзины после скидок и купона
const PriceRoundingFields = observer(() => {
    const step = Number(controller.project.price_rounding_step ?? 0)

    return <>
        <FieldBlock label={i.t('settings:project.priceRounding.step')}>
            <p style={{ marginBottom: 8 }}>{i.t('settings:project.priceRounding.hint')}</p>
            <Select
                value={step}
                style={{ width: '100%' }}
                onChange={value => {
                    controller.set('price_rounding_step', value)
                    if (value > 0 && !controller.project.price_rounding_mode) controller.set('price_rounding_mode', 'math')
                }}
                options={PRICE_ROUNDING_STEPS.map(value => ({
                    value,
                    label: value === 0 ? i.t('settings:project.priceRounding.off') : i.t('settings:project.priceRounding.to', { step: value }),
                }))}
            />
        </FieldBlock>
        {step > 0 &&
            <SelectField
                label={i.t('settings:project.priceRounding.mode')}
                name="price_rounding_mode"
                options={(['math', 'down', 'up'] as const).map(value => ({ value, label: i.t(`settings:project.priceRounding.modes.${value}`) }))}
            />
        }
    </>
})

const PlacesSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<EnvironmentOutlined />} text={i.t('settings:project.places')} background={DARK_BADGE} />
        <MasterToggle>
            <ProjectToggle label={i.t('settings:project.citiesEnabled')} name="cities_enabled" plan={2} />
        </MasterToggle>
        {!!controller.project.cities_enabled &&
            <SelectField label={i.t('settings:project.citiesView')} name="cities_view" options={viewOptions()} />
        }
        <MasterToggle>
            <ProjectToggle label={i.t('settings:project.warehousesEnabled')} name="warehouses_enabled" plan={2} />
        </MasterToggle>
        {!!controller.project.warehouses_enabled &&
            <Row gutter={[16, 16]}>
                <Col span={24} md={12}>
                    <SelectField label={i.t('settings:project.warehousesView')} name="warehouses_view" options={viewOptions()} />
                </Col>
                <Col span={24} md={12}>
                    <SelectField
                        label={i.t('settings:project.warehousesSelectType')}
                        name="warehouse_selection_type"
                        options={[
                            {label: i.t('settings:project.selectOptions.list'), value: 'list'},
                            {label: i.t('settings:project.selectOptions.onMap'), value: 'map'}
                        ]}
                    />
                </Col>
            </Row>
        }
        <SelectField
            label={i.t('settings:project.warehouseSortType')}
            name="wh_sort_type"
            options={[
                {label: i.t('settings:project.warehouseSort.byDistanceNoStock'), value: 0},
                {label: i.t('settings:project.warehouseSort.byListNoStock'), value: 1},
                {label: i.t('settings:project.warehouseSort.byDistanceWithStock'), value: 2},
                {label: i.t('settings:project.warehouseSort.byListWithStock'), value: 3}
            ]}
        />
        <ProjectToggle label={i.t('settings:project.warehousesShowOtherWarehouses')} name="warehouses_show_other_warehouses" />
        <ProjectToggle label={i.t('settings:project.warehouseSelectRequire')} name="warehouse_select_require" />
    </div>
))

const CatalogSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<AppstoreOutlined />} text={i.t('settings:project.sections.catalog')} background={PRIMARY_BADGE} />
        <ProjectToggle label={i.t('settings:project.warehousesShowAllProducts')} name="warehouses_show_all_products" />
        <ToggleField
            label={i.t('settings:project.trackProductQuantity')}
            checked={!!settingsController.project.productcount}
            onChange={e => settingsController.onChangeProject('productcount', e)}
        />
        <ProjectToggle label={i.t('product:allowAddingToCart')} name="allow_adding_to_cart" />
        <ProjectToggle label={i.t('def:Auto select variant')} name="auto_select_variant" />
        <ProjectToggle label={i.t('settings:project.showProductWeight')} name="weight_enabled" />
        <ProjectToggle label={i.t('settings:project.searchSuggestions')} name="search_suggestions" plan={2} />
        {OPTION_KEYS.map(key => <OptionToggle key={key} optionKey={key} />)}
        <FieldBlock label={i.t('product:buy_button')}>
            <Input
                value={controller.project.buy_button ?? ''}
                onChange={e => controller.set('buy_button', e.target.value)}
            />
        </FieldBlock>
    </div>
))

const DisplaySection = observer(() => {
    const fields = [
        { label: i.t('def:item_display_category_level_1'), name: 'view_categories_level_1', options: categoryDisplays() },
        { label: i.t('def:item_display_category_level_2'), name: 'view_categories_level_2', options: categoryDisplays() },
        { label: i.t('def:item_display_category_level_3'), name: 'view_categories_level_3', options: categoryDisplays() },
        { label: i.t('def:item_display_products'), name: 'view_products', options: numberOptions(13) },
        { label: i.t('def:item_display_products_home'), name: 'view_products_in_home', options: numberOptions(8) },
        { label: i.t('def:product_view_types_images'), name: 'product_view_types_images', options: [
            {label: i.t('def:Usual'), value: 0},
            {label: i.t('def:Enlarged'), value: 1},
        ] },
        { label: i.t('settings:project.linkedProductsDisplayType'), name: 'display_type_links_products', options: [
            {value: 0, label: i.t('settings:project.name')},
            {value: 1, label: i.t('def:image.image')},
            {value: 2, label: i.t('settings:project.photoPlusName')},
            {value: 3, label: i.t('settings:project.colorPlusNameNoScroll')},
        ] },
        { label: i.t('settings:project.upsellDisplayType'), name: 'upselling_view_type', options: numberOptions(8) },
        { label: i.t('def:view_articles_level_1'), name: 'view_articles_level_1', options: viewOptions() },
        { label: i.t('def:Places Groups Display Type'), name: 'place_groups_view', options: viewOptions() },
        { label: i.t('settings:project.tapBarDisplayType'), name: 'tap_bar_view_type', options: [
            {value: 0, label: i.t('settings:project.tapBar.classic')},
            {value: 1, label: i.t('settings:project.tapBar.accentOnCart')},
            {value: 2, label: i.t('settings:project.tapBar.classicPlusCart')},
        ] },
        { label: i.t('settings:project.headerDisplayType'), name: 'web_header_view_type', options: [
            {value: 0, label: i.t('settings:project.cartDisplay.standard')},
            {value: 1, label: i.t('settings:project.cartDisplay.simplified')},
        ] },
        { label: i.t('settings:project.cartDisplayType'), name: 'web_header_view_type_cart', options: [
            {value: 0, label: i.t('settings:project.cartDisplay.standard')},
            {value: 1, label: i.t('settings:project.cartDisplay.simplified')},
        ] },
    ]

    return (
        <div style={cardStyle}>
            <SectionBadge icon={<LayoutOutlined />} text={i.t('def:item_display')} background={DARK_BADGE} />
            <Row gutter={[16, 16]}>
                {fields.map(field => (
                    <Col key={field.name} span={24} md={12} xl={8}>
                        <SelectField label={field.label} name={field.name} options={field.options} />
                    </Col>
                ))}
            </Row>
        </div>
    )
})

const ScanCouponsSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge
            icon={<ScanOutlined />}
            text={i.t('def:Scan coupons')}
            background={PRIMARY_BADGE}
            toggle={
                <ErrorByPricingPlan plan={2} content={
                    <Switch
                        checked={!!controller.project.scan_coupon_enabled}
                        onChange={e => controller.set('scan_coupon_enabled', e)}
                        checkedChildren={i.t('def:on')}
                        unCheckedChildren={i.t('def:off')}
                        disabled={storeLocal.get('pricing_plan') < 2}
                    />
                } />
            }
        />
        <DimGroup active={!!controller.project.scan_coupon_enabled}>
            <SelectField
                label={i.t('def:scan_coupon_type')}
                name="scan_coupon_type"
                options={[
                    {label: "QR Code", value: 'qr'},
                    {label: "Barcode", value: 'barcode'},
                ]}
            />
            <SelectField
                label={i.t('def:scan_coupon_value')}
                name="scan_coupon_value"
                options={[
                    {label: i.t('def:Coupon code'), value: 'code'},
                    {label: i.t('def:id_crm'), value: 'id_crm'},
                ]}
            />
        </DimGroup>
    </div>
))

const PushSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<BellOutlined />} text={i.t('def:pushesSettings')} background={DARK_BADGE} />
        <FieldBlock label={i.t('def:Firebase json')}>
            <Input.TextArea
                rows={8}
                value={controller.project.firebase_json ?? ''}
                onChange={e => controller.set('firebase_json', e.target.value)}
            />
        </FieldBlock>
    </div>
))

const MessengersSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<MessageOutlined />} text={i.t('settings:project.sections.messengers')} background={DARK_BADGE} />
        <SelectField
            label={i.t('def:messenger_type')}
            name="messenger_type"
            options={[
                {label: 'Telegram', value: 'telegram'},
                {label: 'MAX', value: 'max'},
            ]}
        />
        <FieldBlock label={i.t('def:telegram_bot_token')}>
            <Input
                value={controller.project.telegram_bot_token}
                onChange={e => controller.set('telegram_bot_token', e.target.value)}
                suffix={
                    <Tooltip title={i.t('def:botTelegram')}>
                        <InfoCircleOutlined style={{ color: 'var(--text-muted-color)' }} />
                    </Tooltip>
                }
            />
        </FieldBlock>
        <ProjectToggle label={i.t('def:Share product')} name="share_product" />
        <Row gutter={[16, 16]}>
            <Col span={24} md={12}>
                <FieldBlock label={i.t('def:deeplink')}>
                    <Input
                        value={controller.project.deeplink ?? ''}
                        onChange={e => controller.set('deeplink', e.target.value)}
                    />
                </FieldBlock>
            </Col>
            <Col span={24} md={12}>
                <FieldBlock label={i.t('def:share_text')}>
                    <Input
                        value={controller.project.share_text ?? ''}
                        onChange={e => controller.set('share_text', e.target.value)}
                    />
                </FieldBlock>
            </Col>
        </Row>
    </div>
))

const ClientsSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<UserOutlined />} text={i.t('settings:project.sections.clients')} background={PRIMARY_BADGE} />
        <ProjectToggle label={i.t('def:Prohibit registration')} name="prohibit_registration" />
        <FieldBlock label={i.t('def:Text for blocked user')}>
            <Input.TextArea
                rows={3}
                value={controller.project.text_for_blocked_user ?? ''}
                onChange={e => controller.set('text_for_blocked_user', e.target.value)}
            />
        </FieldBlock>
        <ProjectToggle label={i.t('settings:project.sendOrderEmailsToClients')} name="email_send_to_clients" />
        <ProjectToggle label={i.t('settings:project.useTransportCompanies')} name="use_tk" />
        <ProjectToggle label={i.t('def:Order check number description')} name="order_check_number" />
        <FieldBlock label={i.t('def:assembly_error')}>
            <p style={{ marginBottom: 8 }}>{i.t('def:assembly_error_description')}</p>
            <InputNumber
                style={{ width: '100%' }}
                value={controller.project.assembly_error ?? 10}
                onChange={e => controller.set('assembly_error', e)}
            />
        </FieldBlock>
    </div>
))

const MaintenanceSection = () => (
    <div style={cardStyle}>
        <SectionBadge icon={<ToolOutlined />} text={i.t('settings:project.sections.maintenance')} background={WARNING_BADGE} />
        <ActionField label={i.t('def:reIndexCatalog')}>
            <Button onClick={() => controller.reIndexCatalog()} danger>{i.t('def:start')}</Button>
        </ActionField>
        <ActionField label={i.t('def:catalogClear')}>
            <Popconfirm
                title={i.t('def:catalogClearText')}
                onConfirm={() => controller.clearAllCatalog()}
                okText={i.t('def:yes')}
                cancelText={i.t('def:no')}
            >
                <Button danger type="primary">{i.t('def:clear')}</Button>
            </Popconfirm>
        </ActionField>
    </div>
)

const PrivateSection = observer(() => (
    <div style={cardStyle}>
        <SectionBadge icon={<LockOutlined />} text={i.t('settings:project.privateSection')} background={DARK_BADGE} />
        <ProjectToggle label={i.t('settings:project.newSearch')} name="new_search" />
        <ActionField label={i.t('settings:project.clickhouseReindex')}>
            <Button onClick={() => controller.clickhouseReindex()} danger>{i.t('def:start')}</Button>
        </ActionField>
        <ActionField label={i.t('superAdmin:hub.fields')}>
            <Button href="/super/fields">{i.t('def:actions.go')}</Button>
        </ActionField>
        <ActionField label={i.t('superAdmin:hub.projects')}>
            <Button href="/super/projects">{i.t('def:actions.go')}</Button>
        </ActionField>
    </div>
))

export const SettingsProject = observer(() => {
    useEffect(() => {
        controller.get()
        controller.getTimezones()
        settingsController.getProject()
        priceTypesController.get()
        userController.decrypt('user')
          .then(user => {
              if(user.super) controller.user_super = true
          })

        OptionTypesController.get([], OPTION_KEYS)
          .then((keys: string[]) => OptionValuesController.get(keys))
    }, [])

    const buttons = [
        { title: i.t('def:actions.save'), htmlType: `submit`, type: 'primary' }
    ]

    return OptionValuesController.render ? (
        <form onSubmit={e => {
            controller.save(e, true)
            OptionValuesController.save(e)
        }}>
            <Titles
                title={i.t('settings:project.title')}
                buttons={buttons}
                breadcrumb={[
                    {title: i.t('def:settings'), href: '/settings'},
                    {title: i.t('settings:project.title')},
                ]}
            />

            <Row gutter={[20, 20]}>
                <Col span={24} xl={12}><GeneralSection /></Col>
                <Col span={24} xl={12}><PricesSection /></Col>

                <Col span={24} xl={12}><PlacesSection /></Col>
                <Col span={24} xl={12}><CatalogSection /></Col>

                <Col span={24}><DisplaySection /></Col>

                <Col span={24} xl={12}><ScanCouponsSection /></Col>
                <Col span={24} xl={12}><PushSection /></Col>

                <Col span={24} xl={12}><MessengersSection /></Col>
                <Col span={24} xl={12}><ClientsSection /></Col>

                <Col span={24} xl={12}><MaintenanceSection /></Col>
                {controller.user_super &&
                    <Col span={24} xl={12}><PrivateSection /></Col>
                }
            </Row>
        </form>
    ) : <></>
})
